/**
 * The in-world **admin panel** (ADR-0183): a left-edge, full-height slide-out with
 * movement controls and temporary character-size controls. It is a sibling of the
 * top-right toolbar panels
 * (`wardrobe.ts`/`settings.ts`) but deliberately *not* a member of their
 * mutually-exclusive corner group: it slides from the left and is admin-gated.
 *
 * **Reveal-only, never authority.** The panel is shown only when the server tells the
 * client this connection is an admin (`Welcome.isAdmin`); `setAvailable(false)` force-
 * closes it and makes the F10 toggle inert. But revealing the UI grants **no power** -
 * every command it sends is re-authorized on the server against the socket's own
 * passcode-proven identity, so a non-admin who forces the panel open in devtools
 * accomplishes nothing (the server rejects `AdminSetNoClip`/`AdminTeleport` for them).
 *
 * Like `settings.ts`, this module owns **only** the DOM wiring; the markup + styling
 * live in `index.html`, and it touches no Pixi and no socket (the callbacks do), so it
 * unit-tests headlessly (jsdom). Built with `textContent` only - the XSS-safe discipline.
 */

import {
  COMBAT_LAB_SCENARIO_IDS,
  combatLabScenario,
  type CombatLabScenarioId,
} from './sim/combatLab';
import {
  ADMIN_MOUNT_OPTIONS,
  adminMountRiderHeight,
  adminMountShowsRider,
  type AdminMount,
  type AdminMountRiderHeight,
} from './adminMounts';

/** Options for {@link initAdminPanel}; `doc` is injected by tests. */
export interface AdminPanelOptions {
  /** Called with the new no-clip state when the toggle is clicked (-> the client sender). */
  onToggleNoClip: (enabled: boolean) => void;
  /** Called when the Teleport button is clicked (-> arm the tile-target cursor). */
  onArmTeleport: () => void;
  /** Called when a character-size button is clicked. */
  onSetSize: (size: 'grow' | 'shrink' | 'normal') => void;
  /** Called when a Combat Lab scenario shortcut is clicked. */
  onStartCombatLab?: (scenario: CombatLabScenarioId) => void;
  /** Called when the oversized bascinet button is clicked. */
  onEquipBascinet: () => void;
  /** Called with the selected mount, or no value when the active mount is toggled off. */
  onSetMount: (mount: AdminMount | undefined) => void;
  /** Applies temporary, client-local absolute rider lifts while an admin tunes a mount. */
  onTuneMountRiderHeight?: (
    mount: AdminMount,
    gait: 'moving' | 'idle',
    offsetY: number,
  ) => void;
  /** Removes temporary tuning and restores the registry's exact rider lifts. */
  onResetMountRiderHeight?: (mount: AdminMount) => void;
  /** The document to wire (defaults to the live `document`); injected by tests. */
  doc?: Document;
}

/** The handle `main.ts`/tests drive the admin panel through. */
export interface AdminPanelHandle {
  /**
   * Reveal (true) or hide (false) the panel. `false` force-closes it, makes {@link toggle}
   * a no-op, and disarms - so a reconnect that loses admin re-hides everything. Called
   * off the reveal-only `Welcome.isAdmin` signal.
   */
  setAvailable(admin: boolean): void;
  /** The F10 handler: slide the panel open/closed. A no-op unless available (admin). */
  toggle(): void;
  /** Whether the panel is currently slid open. */
  isOpen(): boolean;
  /** Reflect the server-confirmed no-clip state on the toggle button (no callback fired). */
  setNoClip(on: boolean): void;
}

export function initAdminPanel(opts: AdminPanelOptions): AdminPanelHandle {
  const doc = opts.doc ?? document;
  const panel = doc.getElementById('admin-panel');
  const noClipBtn = doc.getElementById('admin-noclip-button') as HTMLButtonElement | null;
  const teleportBtn = doc.getElementById('admin-teleport-button') as HTMLButtonElement | null;
  const growBtn = doc.getElementById('admin-grow-button') as HTMLButtonElement | null;
  const shrinkBtn = doc.getElementById('admin-shrink-button') as HTMLButtonElement | null;
  const normalSizeBtn = doc.getElementById('admin-normal-size-button') as HTMLButtonElement | null;
  const combatLabActions = doc.getElementById('admin-combat-lab-actions');
  const bascinetBtn = doc.getElementById('admin-bascinet-button') as HTMLButtonElement | null;
  const mountActions = doc.getElementById('admin-mount-actions');
  const mountRiderTuning = doc.getElementById('admin-mount-rider-tuning');
  const mountRiderTuningName = doc.getElementById('admin-mount-rider-tuning-name');
  const movingSlider = doc.getElementById('admin-mount-moving-slider') as HTMLInputElement | null;
  const idleSlider = doc.getElementById('admin-mount-idle-slider') as HTMLInputElement | null;
  const movingValue = doc.getElementById('admin-mount-moving-value') as HTMLOutputElement | null;
  const idleValue = doc.getElementById('admin-mount-idle-value') as HTMLOutputElement | null;
  const resetRiderHeight = doc.getElementById('admin-mount-rider-reset') as HTMLButtonElement | null;
  const mountButtons = new Map<AdminMount, HTMLButtonElement>();
  for (const mount of ADMIN_MOUNT_OPTIONS) {
    if (!mountActions) break;
    const button = doc.createElement('button');
    button.id = `admin-${mount.id}-button`;
    button.type = 'button';
    button.className = 'admin-mount-button';
    button.setAttribute('aria-pressed', 'false');
    button.title = `Ride a ${mount.label}; press again to dismount`;
    mountActions.append(button);
    mountButtons.set(mount.id, button);
  }

  // Reveal gate (admin) and slide state. Both default off/closed: pre-login and for a
  // non-admin the panel is hidden and inert.
  let available = false;
  let open = false;
  // The button's reflected no-clip state. This is a display mirror only - the server is
  // authoritative (it rejects the underlying command for a non-admin regardless).
  let noClip = false;
  // The selected mount is a display mirror on the same terms as `noClip` above.
  // The server validates and owns the replicated result.
  let selectedMount: AdminMount | undefined;
  const tunedRiderHeights = new Map<AdminMount, AdminMountRiderHeight>();

  const currentRiderHeight = (mount: AdminMount): AdminMountRiderHeight =>
    tunedRiderHeights.get(mount) ?? adminMountRiderHeight(mount);

  const renderRiderTuning = (): void => {
    if (!mountRiderTuning) return;
    mountRiderTuning.hidden =
      selectedMount === undefined || !adminMountShowsRider(selectedMount);
    if (mountRiderTuning.hidden || selectedMount === undefined) return;
    const option = ADMIN_MOUNT_OPTIONS.find((mount) => mount.id === selectedMount)!;
    const height = currentRiderHeight(selectedMount);
    if (mountRiderTuningName) mountRiderTuningName.textContent = option.label;
    if (movingSlider) movingSlider.value = String(height.movingOffsetY);
    if (idleSlider) idleSlider.value = String(height.idleOffsetY);
    if (movingValue) movingValue.value = `${height.movingOffsetY} px`;
    if (idleValue) idleValue.value = `${height.idleOffsetY} px`;
  };

  const applyRiderTuning = (gait: 'moving' | 'idle'): void => {
    if (!available || selectedMount === undefined || !movingSlider || !idleSlider) return;
    const height = {
      movingOffsetY: Number(movingSlider.value),
      idleOffsetY: Number(idleSlider.value),
    };
    tunedRiderHeights.set(selectedMount, height);
    renderRiderTuning();
    opts.onTuneMountRiderHeight?.(
      selectedMount,
      gait,
      gait === 'moving' ? height.movingOffsetY : height.idleOffsetY,
    );
  };

  const renderMountButton = (mount: AdminMount, label: string): void => {
    const button = mountButtons.get(mount);
    if (!button) return;
    const active = selectedMount === mount;
    button.textContent = label;
    button.setAttribute('aria-pressed', String(active));
    button.classList.toggle('active', active);
  };

  const renderMountButtons = (): void => {
    for (const mount of ADMIN_MOUNT_OPTIONS) renderMountButton(mount.id, mount.label);
  };

  const renderNoClipButton = (): void => {
    if (!noClipBtn) return;
    noClipBtn.textContent = noClip ? 'No-clip: On' : 'No-clip: Off';
    noClipBtn.setAttribute('aria-pressed', String(noClip));
    noClipBtn.classList.toggle('active', noClip);
  };

  const render = (): void => {
    if (!panel) return;
    // Hidden entirely for a non-admin; for an admin it stays in the DOM and slides via
    // the `.open` class (off-screen by default, so a closed panel never overlaps play).
    panel.hidden = !available;
    panel.classList.toggle('open', available && open);
  };

  const setOpen = (next: boolean): void => {
    if (!available) next = false; // can never open while not revealed
    open = next;
    render();
  };

  const setAvailable = (admin: boolean): void => {
    available = admin;
    if (!admin) {
      open = false; // losing admin force-closes + disarms the F10 toggle
      // A reconnect that loses admin also drops the server-side mount (it is
      // session-scoped), so the mirror must not claim the character is still riding.
      selectedMount = undefined;
      renderMountButtons();
      renderRiderTuning();
    }
    render();
  };

  const toggle = (): void => {
    if (!available) return; // F10 is inert for a non-admin
    setOpen(!open);
  };

  const setNoClip = (on: boolean): void => {
    noClip = on;
    renderNoClipButton();
  };

  noClipBtn?.addEventListener('click', () => {
    if (!available) return;
    noClip = !noClip;
    renderNoClipButton();
    opts.onToggleNoClip(noClip);
  });
  teleportBtn?.addEventListener('click', () => {
    if (!available) return;
    opts.onArmTeleport();
  });
  growBtn?.addEventListener('click', () => {
    if (!available) return;
    opts.onSetSize('grow');
  });
  shrinkBtn?.addEventListener('click', () => {
    if (!available) return;
    opts.onSetSize('shrink');
  });
  normalSizeBtn?.addEventListener('click', () => {
    if (!available) return;
    opts.onSetSize('normal');
  });
  for (const scenarioId of COMBAT_LAB_SCENARIO_IDS) {
    if (!combatLabActions) break;
    const scenario = combatLabScenario(scenarioId);
    const button = doc.createElement('button');
    button.type = 'button';
    button.className = 'admin-combat-lab-button';
    button.dataset.scenario = scenario.id;
    button.textContent = scenario.label;
    button.title = scenario.description;
    button.addEventListener('click', () => {
      if (!available) return;
      opts.onStartCombatLab?.(scenario.id);
    });
    combatLabActions.append(button);
  }
  bascinetBtn?.addEventListener('click', () => {
    if (!available) return;
    opts.onEquipBascinet();
  });
  const wireMountButton = (button: HTMLButtonElement | undefined, mount: AdminMount): void => {
    button?.addEventListener('click', () => {
      if (!available) return;
      selectedMount = selectedMount === mount ? undefined : mount;
      renderMountButtons();
      renderRiderTuning();
      opts.onSetMount(selectedMount);
    });
  };
  for (const mount of ADMIN_MOUNT_OPTIONS) {
    wireMountButton(mountButtons.get(mount.id), mount.id);
  }
  movingSlider?.addEventListener('input', () => applyRiderTuning('moving'));
  idleSlider?.addEventListener('input', () => applyRiderTuning('idle'));
  resetRiderHeight?.addEventListener('click', () => {
    if (!available || selectedMount === undefined) return;
    tunedRiderHeights.delete(selectedMount);
    renderRiderTuning();
    opts.onResetMountRiderHeight?.(selectedMount);
  });

  renderMountButtons();
  renderRiderTuning();
  renderNoClipButton();
  render();

  return {
    setAvailable,
    toggle,
    isOpen: () => open,
    setNoClip,
  };
}
